if (!customElements.get('shoppable-videos')) {
	customElements.define(
		'shoppable-videos',
		class ShoppableVideos extends HTMLElement {
			constructor() {
				super();
				this.swiper = null;
				this.slides = [];
				this.current = -1;
				this.boundKeydown = this.onKeydown.bind(this);
			}

			connectedCallback() {
				this.cartAddUrl = this.dataset.cartUrl || (window.routes && window.routes.cart_add_url) || '/cart/add';
				this.parseData();
				this.cacheModalEls();
				this.bindTriggers();
				this.bindModal();
				this.initSlider();
				this.initCardPreviews();
			}

			disconnectedCallback() {
				document.removeEventListener('keydown', this.boundKeydown);
				if (this.cardObserver) this.cardObserver.disconnect();
				if (this.boundVisibility) document.removeEventListener('visibilitychange', this.boundVisibility);
				if (this.swiper && this.swiper.destroy) this.swiper.destroy(true, true);
				// Clean up a modal that was relocated to <body> (e.g. on section reload in the editor).
				if (this.modal && this.modal.parentElement === document.body) {
					this.stopMedia();
					this.modal.remove();
				}
			}

			/* ---------------- Data ---------------- */
			parseData() {
				const dataEl = this.querySelector('[data-sbv-data]');
				try {
					this.slides = dataEl ? JSON.parse(dataEl.textContent) : [];
				} catch (e) {
					this.slides = [];
				}
			}

			cacheModalEls() {
				this.modal = this.querySelector('[data-sbv-modal]');
				if (!this.modal) return;
				this.player = this.modal.querySelector('[data-sbv-player]');
				this.video = this.modal.querySelector('[data-sbv-video]');
				this.embed = this.modal.querySelector('[data-sbv-embed]');
				this.progressBar = this.modal.querySelector('[data-sbv-progress-bar]');
				this.muteBtn = this.modal.querySelector('[data-sbv-mute]');
				this.likeBtn = this.modal.querySelector('[data-sbv-like]');
				this.shareBtn = this.modal.querySelector('[data-sbv-share]');
				this.previewPrev = this.modal.querySelector('[data-sbv-preview-prev]');
				this.previewNext = this.modal.querySelector('[data-sbv-preview-next]');
				this.productCard = this.modal.querySelector('[data-sbv-product]');
				this.productImage = this.modal.querySelector('[data-sbv-product-image]');
				this.productTitle = this.modal.querySelector('[data-sbv-product-title]');
				this.productPrice = this.modal.querySelector('[data-sbv-product-price]');
				this.productCompare = this.modal.querySelector('[data-sbv-product-compare]');
				this.productLinks = this.modal.querySelectorAll('[data-sbv-product-link]');
				this.atcBtn = this.modal.querySelector('[data-sbv-atc]');
				this.atcText = this.modal.querySelector('.shop-by-videos__atc-text');
			}

			/* ---------------- Slider ---------------- */
			initSlider() {
				const el = this.querySelector('.shop-by-videos__slider');
				if (!el) return;

				const start = () => {
					if (typeof Swiper === 'undefined') {
						window.addEventListener('load', start, { once: true });
						return;
					}
					if (this.swiper && this.swiper.destroy) this.swiper.destroy(true, true);

					const opts = {
						slidesPerView: parseFloat(this.dataset.slidesMobile) || 1.5,
						spaceBetween: parseInt(this.dataset.space, 10) || 16,
						loop: this.dataset.loop === 'true',
						watchOverflow: true,
						navigation: {
							prevEl: this.querySelector('[data-sbv-prev]'),
							nextEl: this.querySelector('[data-sbv-next]'),
							disabledClass: 'is-disabled',
						},
						breakpoints: {
							750: { slidesPerView: parseFloat(this.dataset.slidesTablet) || 3.5 },
							990: { slidesPerView: parseFloat(this.dataset.slidesDesktop) || 5 },
						},
					};

					if (this.dataset.autoplay === 'true') {
						opts.autoplay = {
							delay: (parseFloat(this.dataset.autoplaySpeed) || 4) * 1000,
							disableOnInteraction: false,
							pauseOnMouseEnter: true,
						};
					}

					this.swiper = new Swiper(el, opts);
				};

				start();
			}

			/* ---------------- Card previews ---------------- */
			initCardPreviews() {
				this.cardVideos = Array.from(this.querySelectorAll('[data-sbv-card-video]'));
				if (!this.cardVideos.length) return;

				const mode = this.dataset.cardPreview || 'always';
				if (mode === 'off') return;

				const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
				if (reduceMotion) return;

				// Touch devices have no hover, so they always fall back to play-in-view.
				const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
				const playInView = mode === 'always' || !canHover;
				this.playInView = playInView;

				// Vertical-only margin: a partially visible edge card in the carousel
				// still counts as in view, so every visible card keeps looping.
				this.cardObserver = new IntersectionObserver(
					(entries) => {
						entries.forEach((entry) => {
							const video = entry.target;
							if (entry.isIntersecting) {
								this.prepareCardVideo(video);
								if (playInView) this.playCardVideo(video);
							} else {
								this.pauseCardVideo(video);
							}
						});
					},
					{ rootMargin: '200px 0px', threshold: 0 }
				);

				this.cardVideos.forEach((video) => {
					this.cardObserver.observe(video);

					if (canHover && mode === 'hover') {
						const card = video.closest('.shop-by-videos__card') || video.parentElement;
						card.addEventListener('mouseenter', () => {
							this.prepareCardVideo(video);
							this.playCardVideo(video);
						});
						card.addEventListener('mouseleave', () => this.pauseCardVideo(video, true));
						card.addEventListener('focusin', () => {
							this.prepareCardVideo(video);
							this.playCardVideo(video);
						});
						card.addEventListener('focusout', () => this.pauseCardVideo(video, true));
					}
				});

				// Don't keep card audio-less loops burning CPU while a tab is hidden.
				this.boundVisibility = () => {
					if (document.hidden) this.pauseAllCardVideos();
					else this.resumeCardVideos();
				};
				document.addEventListener('visibilitychange', this.boundVisibility);
			}

			prepareCardVideo(video) {
				if (video.dataset.loaded === 'true') return;
				const src = video.dataset.src;
				if (!src) return;
				video.src = src;
				video.dataset.loaded = 'true';
			}

			playCardVideo(video) {
				if (this.modalOpen) return;
				this.prepareCardVideo(video);
				video.muted = true;
				const p = video.play();
				// Only reveal the video once playback really started — a rejected
				// play() would otherwise leave a frozen frame over the poster.
				if (p && p.then) {
					p.then(() => video.classList.add('is-playing')).catch(() => {
						video.classList.remove('is-playing');
					});
				} else {
					video.classList.add('is-playing');
				}
			}

			pauseCardVideo(video, rewind) {
				if (!video || video.paused) {
					if (video) video.classList.remove('is-playing');
					return;
				}
				video.pause();
				if (rewind) video.currentTime = 0;
				video.classList.remove('is-playing');
			}

			pauseAllCardVideos() {
				if (this.cardVideos) this.cardVideos.forEach((v) => this.pauseCardVideo(v));
			}

			// After the modal closes (or the tab is shown again) the in-view cards
			// need restarting — the observer won't re-fire for cards that never moved.
			resumeCardVideos() {
				if (!this.playInView || !this.cardVideos) return;
				this.cardVideos.forEach((video) => {
					const rect = video.getBoundingClientRect();
					const inView =
						rect.bottom > 0 &&
						rect.top < (window.innerHeight || document.documentElement.clientHeight) &&
						rect.right > 0 &&
						rect.left < (window.innerWidth || document.documentElement.clientWidth);
					if (inView) this.playCardVideo(video);
				});
			}

			/* ---------------- Triggers ---------------- */
			bindTriggers() {
				this.querySelectorAll('[data-sbv-trigger]').forEach((btn) => {
					btn.addEventListener('click', () => {
						this.open(parseInt(btn.dataset.index, 10));
					});
				});
			}

			bindModal() {
				if (!this.modal) return;

				this.modal.querySelectorAll('[data-sbv-close]').forEach((el) => {
					el.addEventListener('click', () => this.close());
				});

				const prev = this.modal.querySelector('[data-sbv-modal-prev]');
				const next = this.modal.querySelector('[data-sbv-modal-next]');
				if (prev) prev.addEventListener('click', () => this.go(-1));
				if (next) next.addEventListener('click', () => this.go(1));

				if (this.muteBtn) this.muteBtn.addEventListener('click', () => this.toggleMute());
				if (this.likeBtn) this.likeBtn.addEventListener('click', () => this.likeBtn.classList.toggle('is-active'));
				if (this.shareBtn) this.shareBtn.addEventListener('click', () => this.share());
				if (this.atcBtn) this.atcBtn.addEventListener('click', () => this.addToCart());

				if (this.video) {
					this.video.addEventListener('timeupdate', () => {
						if (!this.video.duration) return;
						this.progressBar.style.width = (this.video.currentTime / this.video.duration) * 100 + '%';
					});
				}
			}

			/* ---------------- Open / close ---------------- */
			open(index) {
				if (!this.modal || isNaN(index)) return;
				// Move the modal to <body> so position:fixed is never trapped by a
				// transformed ancestor (this theme uses GSAP/ScrollTrigger globally).
				if (this.modal.parentElement !== document.body) {
					document.body.appendChild(this.modal);
				}
				document.addEventListener('keydown', this.boundKeydown);
				this.modalOpen = true;
				this.pauseAllCardVideos();
				this.modal.setAttribute('aria-hidden', 'false');
				this.modal.classList.add('is-open');
				document.body.classList.add('overflow-hidden');
				this.load(index);
			}

			close() {
				if (!this.modal) return;
				this.modalOpen = false;
				this.resumeCardVideos();
				this.modal.classList.remove('is-open');
				this.modal.setAttribute('aria-hidden', 'true');
				document.body.classList.remove('overflow-hidden');
				document.removeEventListener('keydown', this.boundKeydown);
				this.stopMedia();
				this.current = -1;
			}

			onKeydown(e) {
				if (e.key === 'Escape') this.close();
				else if (e.key === 'ArrowRight') this.go(1);
				else if (e.key === 'ArrowLeft') this.go(-1);
			}

			go(dir) {
				const total = this.slides.length;
				if (!total) return;
				let next = this.current + dir;
				if (next < 0) next = total - 1;
				if (next >= total) next = 0;
				this.load(next);
			}

			pickSource(sources) {
				if (!sources || !sources.length) return null;
				const isMp4 = (s) =>
					s.url && (s.format === 'mp4' || (s.type && s.type.indexOf('mp4') !== -1) || /\.mp4(\?|$)/i.test(s.url));
				return sources.find(isMp4) || sources.find((s) => s.url) || null;
			}

			/* ---------------- Load a slide ---------------- */
			load(index) {
				const data = this.slides[index];
				if (!data) return;
				this.current = index;
				this.stopMedia();

				const mp4 = this.pickSource(data.sources);
				const hasMp4 = !!mp4;
				const hasEmbed = !!data.embed;
				const inner = this.player.querySelector('.shop-by-videos__player-inner');
				const progress = this.player.querySelector('.shop-by-videos__progress');

				// Reset
				this.video.hidden = true;
				this.embed.hidden = true;
				this.embed.innerHTML = '';
				this.progressBar.style.width = '0';
				inner.style.backgroundImage = '';

				if (hasMp4) {
					this.video.hidden = false;
					if (this.muteBtn) this.muteBtn.style.display = '';
					if (progress) progress.style.display = '';
					this.video.poster = data.poster || '';
					this.video.src = mp4.url;
					this.video.muted = true;
					this.player.classList.add('is-muted');
					const playPromise = this.video.play();
					if (playPromise && playPromise.catch) playPromise.catch(() => {});
				} else if (hasEmbed) {
					this.embed.hidden = false;
					if (this.muteBtn) this.muteBtn.style.display = 'none';
					if (progress) progress.style.display = 'none';
					const iframe = document.createElement('iframe');
					iframe.src = data.embed;
					iframe.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture';
					iframe.setAttribute('allowfullscreen', '');
					this.embed.appendChild(iframe);
				} else {
					// poster-only fallback
					if (this.muteBtn) this.muteBtn.style.display = 'none';
					if (progress) progress.style.display = 'none';
					inner.style.backgroundImage = data.poster ? `url("${data.poster}")` : '';
				}

				this.updateNeighbors(index);
				this.updateProduct(data);
			}

			updateNeighbors(index) {
				const total = this.slides.length;
				if (!this.previewPrev || !this.previewNext) return;
				const prev = this.slides[(index - 1 + total) % total];
				const next = this.slides[(index + 1) % total];
				this.previewPrev.style.backgroundImage = prev && prev.poster ? `url("${prev.poster}")` : '';
				this.previewNext.style.backgroundImage = next && next.poster ? `url("${next.poster}")` : '';
			}

			updateProduct(data) {
				if (!this.productCard) return;

				if (!data.hasProduct) {
					this.productCard.hidden = true;
					return;
				}
				this.productCard.hidden = false;

				if (this.productImage) {
					this.productImage.src = data.image || data.poster || '';
					this.productImage.alt = data.title || '';
				}
				if (this.productTitle) this.productTitle.textContent = data.title || '';
				// price strings come from Shopify's money filter (trusted) and may contain HTML
				if (this.productPrice) this.productPrice.innerHTML = data.price || '';
				if (this.productCompare) this.productCompare.innerHTML = data.compareAt || '';

				this.productLinks.forEach((a) => {
					if (data.url) {
						a.href = data.url;
						a.style.pointerEvents = '';
					} else {
						a.removeAttribute('href');
						a.style.pointerEvents = 'none';
					}
				});

				if (this.atcBtn) {
					this.atcBtn.dataset.variant = data.variantId || '';
					const soldOut = !data.available || !data.variantId;
					this.atcBtn.disabled = soldOut;
					if (this.atcText) {
						this.atcText.textContent = soldOut
							? (window.variantStrings && window.variantStrings.soldOut) || 'Sold out'
							: (window.variantStrings && window.variantStrings.addToCart) || 'Add to cart';
					}
				}
			}

			/* ---------------- Media controls ---------------- */
			stopMedia() {
				if (this.video) {
					this.video.pause();
					this.video.removeAttribute('src');
					this.video.load();
				}
				if (this.embed) this.embed.innerHTML = '';
				if (this.player) {
					const inner = this.player.querySelector('.shop-by-videos__player-inner');
					if (inner) inner.style.backgroundImage = '';
				}
			}

			toggleMute() {
				if (!this.video) return;
				this.video.muted = !this.video.muted;
				this.player.classList.toggle('is-muted', this.video.muted);
				if (!this.video.muted && this.video.paused) this.video.play().catch(() => {});
			}

			share() {
				const data = this.slides[this.current];
				const url = data && data.url ? new URL(data.url, window.location.origin).href : window.location.href;
				const title = (data && data.title) || document.title;
				if (navigator.share) {
					navigator.share({ title, url }).catch(() => {});
				} else if (navigator.clipboard) {
					navigator.clipboard.writeText(url).then(() => {
						this.flash(this.shareBtn);
					});
				}
			}

			flash(btn) {
				if (!btn) return;
				btn.classList.add('is-active');
				setTimeout(() => btn.classList.remove('is-active'), 1200);
			}

			/* ---------------- Add to cart ---------------- */
			async addToCart() {
				const variantId = this.atcBtn && this.atcBtn.dataset.variant;
				if (!variantId || this.atcBtn.disabled) return;

				this.atcBtn.classList.add('is-loading');
				this.atcBtn.disabled = true;

				const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
				const body = { id: Number(variantId), quantity: 1 };
				if (cart && typeof cart.getSectionsToRender === 'function') {
					body.sections = cart.getSectionsToRender().map((s) => s.id);
					body.sections_url = window.location.pathname;
				}

				try {
					const res = await fetch(this.cartAddUrl, {
						method: 'POST',
						headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
						body: JSON.stringify(body),
					});
					const json = await res.json();

					if (json.status) {
						// error from Shopify
						console.error(json.description || json.message);
						return;
					}

					if (cart && json.sections && typeof cart.renderContents === 'function') {
						this.close();
						cart.renderContents(json);
					} else {
						window.location = (window.routes && window.routes.cart_url) || '/cart';
					}
				} catch (e) {
					console.error('Add to cart failed', e);
				} finally {
					this.atcBtn.classList.remove('is-loading');
					this.atcBtn.disabled = false;
				}
			}
		}
	);
}
